import { useQueryClient } from "@tanstack/react-query";
import { Building2, Pencil } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Button, ErrorState, Field, Input } from "@/design";
import { ApiError, api, idem, qk, unwrap, useCreateWorkspace, type Me } from "@/lib/api";
import { config } from "@/lib/config";
import { StepHeading } from "./OnboardingFrame";

const SLUG = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;
/** Top-level dashboard routes (web/README.md) can never be workspace addresses. */
const RESERVED = new Set(["login", "signup", "invite", "invitations", "waitlist", "approve", "badge", "design", "new", "settings"]);

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/'s\b/g, "s")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
}

function suggestedName(me: Me | null): string {
  const first = me?.user?.name?.trim().split(/\s+/)[0];
  return first ? `${first}'s workspace` : "";
}

const host = (() => {
  try {
    return new URL(config.publicUrl).host;
  } catch {
    return "app.guv.dev";
  }
})();

export function WorkspaceStep({ me, renameSlug, onCreated }: { me: Me | null; renameSlug: string | null; onCreated: (slug: string) => void }) {
  const qc = useQueryClient();
  const create = useCreateWorkspace();
  const [name, setName] = useState(() => suggestedName(me));
  const [slug, setSlug] = useState(() => slugify(suggestedName(me)));
  const [slugEdited, setSlugEdited] = useState(false);
  const [editingSlug, setEditingSlug] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const nameError = touched && name.trim().length < 2 ? "Give it a name of at least 2 characters." : undefined;
  const slugProblem = !SLUG.test(slug)
    ? "Use 3 to 40 lowercase letters, numbers and hyphens, starting and ending with a letter or number."
    : RESERVED.has(slug)
      ? "That address is reserved. Try another."
      : undefined;
  const apiField = error instanceof ApiError ? error.problem.errors?.[0] : undefined;
  const slugError = (touched && slugProblem) || (apiField?.path === "slug" ? apiField.message : undefined);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (name.trim().length < 2) return;
    if (slugProblem) return setEditingSlug(true);
    setBusy(true);
    setError(null);
    try {
      if (renameSlug) {
        // The server already made a workspace at sign-up: name that one rather than making a second.
        const ws = await unwrap(api.PATCH("/v1/workspaces/{workspace}", { params: { path: { workspace: renameSlug }, header: idem() }, body: { name: name.trim(), slug } }));
        void qc.invalidateQueries({ queryKey: qk.me() });
        onCreated("slug" in ws ? ws.slug : slug);
      } else {
        const ws = await create.mutateAsync({ name: name.trim(), slug });
        onCreated(ws.slug);
      }
    } catch (err) {
      setError(err);
      if (err instanceof ApiError && err.problem.errors?.some((f) => f.path === "slug")) setEditingSlug(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <StepHeading eyebrow="Your passkey is saved" title="Name your workspace">
        A workspace holds your projects, your team and your bill. Most people use their company's name, or their own.
      </StepHeading>

      <div className="mt-8 space-y-5">
        <Field label="Workspace name" error={nameError ?? (apiField?.path === "name" ? apiField.message : undefined)} hint="You can change this at any time.">
          <Input
            value={name}
            autoFocus
            maxLength={100}
            leading={<Building2 />}
            placeholder="Acme Ltd"
            onChange={(e) => {
              setName(e.target.value);
              if (!slugEdited) setSlug(slugify(e.target.value));
            }}
          />
        </Field>

        {editingSlug ? (
          <Field label="Web address" error={slugError} hint="Lowercase letters, numbers and hyphens.">
            <Input
              value={slug}
              maxLength={40}
              spellCheck={false}
              autoCapitalize="off"
              className="font-mono"
              leading={<span className="font-mono text-xs">{host}/</span>}
              // The prefix is 12px monospace: about 7.25px a character, after the 12px inset.
              style={{ paddingLeft: `calc(14px + ${(host.length + 1) * 7.25}px)` }}
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
              }}
            />
          </Field>
        ) : (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-surface-2 px-3 py-2.5 text-sm">
            <span className="text-muted">Your address</span>
            <span className="min-w-0 flex-1 truncate font-mono text-text">
              {host}/<span className="font-semibold">{slug || "…"}</span>
            </span>
            <Button type="button" variant="link" size="sm" icon={<Pencil />} onClick={() => setEditingSlug(true)}>
              Change
            </Button>
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          Continue
        </Button>
      </div>
      {error != null && !apiField && <ErrorState error={error} className="mt-5" />}
      {error != null && apiField && (
        <p className="mt-4 text-sm text-danger-text" role="alert">
          {error instanceof ApiError ? error.problem.title : "That did not work"}. {apiField.message}
        </p>
      )}
    </form>
  );
}
